import {
  Input,
  Stack,
  Text,
  Textarea,
  createListCollection,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import {
  createMessagingPillar,
  updateMessagingPillar,
} from "../api/company-knowledge";
import { useLoading, useToaster } from "../hooks";
import {
  CustomerProfileSubset,
  MessagingPillar,
} from "../types/company-knowledge";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import { Field } from "./field";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "./select";
import { toaster } from "./toaster";

// A pillar passes `pillar` to edit an existing one, or null to create.
// The ICP subsets come from the parent page, which loads them
// alongside the pillars for its "Applicable ICP subsets" column.

const messagingPillarSchema = z.object({
  name: z.string().trim().min(1, "A name is required."),
  type: z.enum(["pain", "benefit", "capability"]),
  description: z.string(),
  "subset-ids": z.array(z.number()),
});

type MessagingPillarFormData = z.infer<typeof messagingPillarSchema>;

const pillarTypeCollection = createListCollection({
  items: [
    {
      value: "pain",
      label: "Pain",
      hint: "A problem the buyer has",
    },
    {
      value: "benefit",
      label: "Benefit",
      hint: "An outcome for the buyer",
    },
    {
      value: "capability",
      label: "Capability",
      hint: "What the product does",
    },
  ],
});

interface MessagingPillarDialogProps {
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onSuccess: () => void;
  pillar?: MessagingPillar | null;
  subsets: CustomerProfileSubset[];
  workspaceSlug: string;
}

export function MessagingPillarDialog({
  isOpen,
  onOpenChange,
  onSuccess,
  pillar,
  subsets,
  workspaceSlug,
}: MessagingPillarDialogProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<MessagingPillarFormData>({
    resolver: zodResolver(messagingPillarSchema),
    defaultValues: emptyPillar,
  });

  React.useEffect(() => {
    if (!isOpen) return;
    reset(pillar ? toFormData(pillar) : emptyPillar);
  }, [isOpen, pillar, reset]);

  const onSubmit = async (data: MessagingPillarFormData) => {
    await withLoading(async () => {
      await withToaster(async () => {
        if (pillar) {
          await updateMessagingPillar(workspaceSlug, pillar.id, data);
          toaster.create({
            type: "success",
            title: "Messaging pillar updated",
          });
        } else {
          await createMessagingPillar(workspaceSlug, data);
          toaster.create({
            type: "success",
            title: "Messaging pillar created",
          });
        }
        onOpenChange({ open: false });
        onSuccess();
      });
    });
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange} size="md">
      <DialogContent ref={ref}>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>
            {pillar ? "Edit Messaging Pillar" : "Create Messaging Pillar"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogBody paddingBottom="6">
            <Stack gap="4">
              <Field
                label="Name"
                invalid={!!errors.name}
                errorText={errors.name?.message}
              >
                <Input
                  size="sm"
                  placeholder="The pillar's name"
                  {...register("name")}
                />
              </Field>
              <Field
                label="Type"
                helperText="Pain is a buyer problem; benefit is an outcome for the buyer; capability is what the product does."
                invalid={!!errors.type}
                errorText={errors.type?.message}
              >
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <SelectRoot
                      size="sm"
                      value={[field.value]}
                      onValueChange={({ value }) => field.onChange(value[0])}
                      onBlur={field.onBlur}
                      collection={pillarTypeCollection}
                    >
                      <SelectTrigger>
                        <SelectValueText />
                      </SelectTrigger>
                      <SelectContent
                        portalRef={ref as React.RefObject<HTMLElement>}
                      >
                        {pillarTypeCollection.items.map((type) => (
                          <SelectItem key={type.value} item={type}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </SelectRoot>
                  )}
                />
              </Field>
              <Field
                label="Describe this pillar"
                helperText="Explain the idea fully, including the buyer's situation and why it matters."
                invalid={!!errors.description}
                errorText={errors.description?.message}
              >
                <Textarea
                  {...register("description")}
                  placeholder="The idea, the buyer's situation, and why it matters."
                  rows={3}
                />
              </Field>
              {/* Deliberately not a <Field>: a Chakra Field shares its
                  label/input ids with every control inside it, which
                  makes each checkbox's label point at the first
                  checkbox's input. Field is for one control; a group
                  gets plain text. */}
              <Stack gap="1">
                <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                  Applicable ICP subsets
                </Text>
                <Controller
                  name="subset-ids"
                  control={control}
                  render={({ field }) => (
                    <Stack gap="2">
                      {subsets.map((subset) => {
                        const checked = field.value.includes(subset.id);
                        return (
                          <Checkbox
                            key={subset.id}
                            checked={checked}
                            onCheckedChange={(details) => {
                              const next = details.checked
                                ? [...field.value, subset.id]
                                : field.value.filter((id) => id !== subset.id);
                              field.onChange(next);
                            }}
                          >
                            {subset.name}
                          </Checkbox>
                        );
                      })}
                      <Text color="fg.muted" fontSize="sm" fontStyle="italic">
                        Applies to all subsets when none are selected.
                      </Text>
                    </Stack>
                  )}
                />
              </Stack>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange({ open: false })}
            >
              Cancel
            </Button>
            <Button loading={isLoading} type="submit">
              {pillar ? "Save Changes" : "Create Messaging Pillar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}

const emptyPillar: MessagingPillarFormData = {
  name: "",
  type: "benefit",
  description: "",
  "subset-ids": [],
};

// The schema's nullable fields arrive as null over the wire; empty
// strings are the form's representation of "unset".
const toFormData = (pillar: MessagingPillar): MessagingPillarFormData => ({
  name: pillar.name ?? "",
  type: pillar.type ?? "benefit",
  description: pillar.description ?? "",
  "subset-ids": pillar["subset-ids"] ?? [],
});
